/**
 * NL-024 + NL-027 — cross-cutting effects for `/tools/*` routes, mounted once
 * in AppLayout. Keeps the 30 bespoke tool forms untouched:
 *
 *   NL-027 (coach edit): mirror the URL's `?coachEdit=<token>` into the
 *     module singleton for the lifetime of the tool route, so
 *     `submitToolCompletion` transparently routes saves to the coach-edit
 *     endpoint. Cleared on navigate-away / unmount.
 *
 *   NL-024 (read-only redirect): when a client lands on an editable tool
 *     route whose completion is already finalized — and no coach-edit session
 *     is open — redirect to the read-only `/tools/<key>/view`. This is the
 *     "finalize-once" lock the FinalizeWarning promises, applied uniformly
 *     instead of per-route.
 *
 * Both keyed off `editableToolRouteFor(pathname)`, so non-tool routes (and the
 * `/view` route itself) are inert.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import { getToolCompletion } from '@/lib/api-client';
import { setActiveCoachEdit } from '@/lib/coach-edit-token';
import { useClientId } from '@/lib/use-client-id';
import { editableToolRouteFor } from '@/lib/tool-routes';

export function useToolRouteEffects(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const rawCoachEdit = searchParams.get('coachEdit');
  const coachEditToken = rawCoachEdit && rawCoachEdit.length > 0 ? rawCoachEdit : null;
  const pathname = location.pathname;

  // NL-027 — keep the coach-edit session in sync with the URL for the life
  // of the tool route. Scoped to (clientId, canonical toolKey) so the token
  // only applies to a save for that exact client+tool. Only armed on an
  // actual editable tool route, so a stray `?coachEdit` elsewhere is inert.
  useEffect(() => {
    const route = editableToolRouteFor(pathname);
    setActiveCoachEdit(
      route && coachEditToken && clientId
        ? { token: coachEditToken, clientId, toolKey: route.completionKey }
        : null,
    );
    return () => setActiveCoachEdit(null);
  }, [pathname, coachEditToken, clientId]);

  // NL-024 — redirect a finalized editable tool to its read-only /view,
  // unless a coach-edit session is active (the coach wants the form editable).
  useEffect(() => {
    const route = editableToolRouteFor(pathname);
    if (!route || !clientId || coachEditToken) return;
    let active = true;
    void (async () => {
      try {
        const completion = await getToolCompletion(accessToken, clientId, route.completionKey);
        if (active && completion && completion.status === 'completed') {
          navigate(
            `/tools/${encodeURIComponent(route.completionKey)}/view?clientId=${encodeURIComponent(clientId)}`,
            { replace: true },
          );
        }
      } catch {
        /* swallow — leave the client on the editable form */
      }
    })();
    return () => {
      active = false;
    };
  }, [pathname, clientId, coachEditToken, accessToken, navigate]);
}
